import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CheckCircle, Clock, Funnel } from '@phosphor-icons/react';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal for the Withdraw Group's three-dot menu — combined History + Filters.
 * Shows entries per date + status (paid / pending / all).
 * Clicking an entry toggles paid <-> pending (admin + handler role).
 */
const WithdrawHistoryModal = ({ open, onClose, actorId, actorRole, onJumpToMessage }) => {
  const [tab, setTab] = useState('all'); // all | pending | paid
  const [date, setDate] = useState('');
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, paid: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set()); // entry ids selected for bulk
  const [bulkBusy, setBulkBusy] = useState(false);

  const canToggle = actorRole && ['super_admin', 'withdrawal_handler', 'deposit_handler'].includes(actorRole);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'all') params.set('status', tab);
      if (date) params.set('date', date);
      const res = await axios.get(`${API}/withdraw-entries?${params.toString()}`);
      setEntries(res.data?.entries || []);
      setCounts(res.data?.counts || { pending: 0, paid: 0, total: 0 });
    } catch (e) {
      toast.error('Failed to load entries');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, tab, date]);

  // Clear selection whenever tab/date changes or modal reopens
  useEffect(() => { setSelected(new Set()); }, [open, tab, date]);

  const toggleSel = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const pendingEntries = entries.filter(e => e.status === 'pending');
  const selectablePending = pendingEntries.filter(e => selected.has(e.id));

  const selectAllPending = () => {
    if (pendingEntries.length === 0) return;
    const allSelected = pendingEntries.every(e => selected.has(e.id));
    setSelected(prev => {
      const n = new Set(prev);
      if (allSelected) pendingEntries.forEach(e => n.delete(e.id));
      else pendingEntries.forEach(e => n.add(e.id));
      return n;
    });
  };

  const bulkMarkPaid = async () => {
    if (selectablePending.length === 0) { toast.error('Select at least one pending entry'); return; }
    if (!window.confirm(`Mark ${selectablePending.length} entries as PAID?`)) return;
    setBulkBusy(true);
    const ids = selectablePending.map(e => e.id);
    try {
      const res = await axios.post(`${API}/withdraw-entries/bulk-mark-paid`, {
          actor_id: actorId, ids, status: 'paid'
        });
      const okN = res.data?.updated ?? ids.length;
      setSelected(new Set());
      // 3-second undo toast — revert to pending atomically
      let undone = false;
      toast(`✓ Marked ${okN} paid`, {
        duration: 3000,
        action: {
          label: 'UNDO',
          onClick: async () => {
            undone = true;
            try {
              await axios.post(`${API}/withdraw-entries/bulk-mark-paid`, {
                  actor_id: actorId, ids, status: 'pending'
                });
              toast.success('Bulk revert done — all reverted to pending');
              await load();
            } catch (err) {
              toast.error('Undo failed');
            }
          }
        }
      });
      // Refresh after settle window
      setTimeout(() => { if (!undone) load(); }, 3200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk mark-paid failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const togglePaid = async (entry) => {
    if (!canToggle) { toast.error('Only admin/handler can update status'); return; }
    const target = entry.status === 'paid' ? 'pending' : 'paid';
    try {
      await axios.post(`${API}/withdraw-entries/${entry.id}/mark-paid`, { actor_id: actorId, status: target });
      toast.success(target === 'paid' ? 'Marked as Paid ✓' : 'Reverted to Pending');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden shadow-2xl" data-testid="withdraw-history-modal">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-amber-500 text-white">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Funnel size={20} weight="bold" /> Withdraw History</h3>
            <p className="text-[11px] opacity-90 mt-0.5">
              {counts.pending} Pending · {counts.paid} Paid · {counts.total} Total
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full" data-testid="withdraw-history-close-btn"><X size={22} /></button>
        </div>

        {/* Filters */}
        <div className="p-3 border-b bg-gray-50 space-y-2">
          <div className="flex gap-1.5">
            {[
              { key: 'all', label: `All (${counts.total})` },
              { key: 'pending', label: `Pending (${counts.pending})`, color: '#f59e0b' },
              { key: 'paid', label: `Paid (${counts.paid})`, color: '#10b981' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`withdraw-history-tab-${t.key}`}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                  tab === t.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Date:</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="flex-1 px-2 py-1 border rounded text-xs"
              data-testid="withdraw-history-date-input"
            />
            {date && (
              <button onClick={() => setDate('')} className="text-xs text-blue-600">Clear</button>
            )}
          </div>

          {/* Session-32 (Feb-15-2026): Bulk-mark-paid + inline toggle REMOVED
              per user spec — the History & Filter view is now READ-ONLY. It
              exists only to see paid/pending counts and jump to the original
              group message. Mark-as-Paid must be done on the message itself
              (opens the confirmation popup with UTR + bank details). */}
        </div>

        {/* Entries list */}
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">No entries found</div>
          ) : (
            <div className="space-y-2">
              {entries.map(e => {
                const isPaid = e.status === 'paid';
                return (
                  <div
                    key={e.id}
                    data-testid={`withdraw-entry-${e.id}`}
                    className={`w-full rounded-lg border p-3 transition-all flex gap-2 ${
                      isPaid
                        ? 'bg-green-50 border-green-300'
                        : 'bg-white border-amber-200 hover:bg-amber-50'
                    }`}
                  >
                    <button
                      onClick={() => {
                        // Read-only history: click always jumps to the group
                        // message. Never toggles paid inline (session-32).
                        if (onJumpToMessage && e.group_message_id) {
                          onJumpToMessage(e.group_message_id);
                          if (onClose) onClose();
                        }
                      }}
                      className="flex-1 text-left min-w-0"
                      data-testid={`withdraw-entry-jump-${e.id}`}
                    >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isPaid ? (
                            <CheckCircle size={16} weight="fill" className="text-green-600" />
                          ) : (
                            <Clock size={16} weight="fill" className="text-amber-500" />
                          )}
                          <span className="font-semibold text-sm text-gray-800 truncate">{e.customer_username}</span>
                          <span className="text-[10px] text-gray-500">{e.customer_phone}</span>
                        </div>
                        <div className="text-lg font-bold mt-0.5" style={{ color: isPaid ? '#059669' : '#d97706' }}>
                          ₹ {e.amount}
                        </div>
                        {(e.bank_snapshot?.account_holder_name || e.bank_snapshot?.upi_id) && (
                          <div className="text-[11px] text-gray-600 mt-1 leading-tight">
                            {e.bank_snapshot?.account_holder_name && <div>{e.bank_snapshot.account_holder_name} · {e.bank_snapshot.bank_name}</div>}
                            {e.bank_snapshot?.bank_account_number && <div className="font-mono">A/c: {e.bank_snapshot.bank_account_number} · IFSC: {e.bank_snapshot.ifsc_code}</div>}
                            {e.bank_snapshot?.upi_id && <div>UPI: {e.bank_snapshot.upi_id}</div>}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          isPaid ? 'bg-green-600 text-white' : 'bg-amber-500 text-white'
                        }`}>
                          {isPaid ? '✓ Paid' : 'Pending'}
                        </span>
                        <div className="text-[10px] text-gray-500 mt-1">
                          {new Date(e.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                    {canToggle && (
                      <div className="text-[10px] text-gray-400 mt-2 text-center italic">Tap to {isPaid ? 'revert to pending' : 'mark as paid'}</div>
                    )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawHistoryModal;
